import { TypeManager } from '@dipscope/type-manager';
import KEnv from '@envclient/envengine/src/envs/koenigsburg/KEnv';
import { KTileDTO } from '@envclient/envengine/src/envs/koenigsburg/KTile';
import { getKTileDef } from '../../config/kTileMapConfig';

/**
 * Predicted score per player: sum, across every city (hexa tile) currently on the board, of that
 * city's earnable points (gates + mineCount) for every player CURRENTLY leading it - the same
 * "earnable points" and "who's leading" logic KTileItem.tsx already uses to draw the per-city score
 * badge (colored circle(s) in a city's lower corner). A draw credits the full amount to every tied
 * leader, matching the badge showing one circle per tied leader there too.
 *
 * Deserializes `clone` independently (same field KTileMap.tsx builds its whole board from) rather
 * than sharing KTileMap's local KEnv instance, since the player list lives outside KTileMap's
 * component tree and only has access to the redux env DTO - see KEnv.data()'s comment on why clone
 * is unconditional (not bot-only) for koenigsburg specifically.
 */
export function computePredictedScores(clone: { [key: string]: unknown } | undefined): Record<number, number> {
  const scores: Record<number, number> = {};
  if (!clone) return scores;
  let kEnv: KEnv;
  try {
    kEnv = TypeManager.deserialize(KEnv, clone) as KEnv;
  } catch (e) {
    return scores;
  }
  const tiles = kEnv.tileLogic.tiles() as KTileDTO[];
  for (const tile of tiles) {
    if (tile.shape !== 'hexa') continue;
    const tileDef = getKTileDef(tile.type);
    const gates = tileDef.nodes.filter((n) => n.type === 'r').length;
    const points = gates + (tile.mineCount ?? 0);
    const resources = tile.playerResource ? Array.from(tile.playerResource.map.values()) : [];
    if (resources.length === 0) continue;
    const maxScore = Math.max(...resources.map((r) => r.score));
    if (maxScore <= 0) continue;
    resources
      .filter((r) => r.score === maxScore)
      .forEach((leader) => {
        scores[leader.playerId] = (scores[leader.playerId] ?? 0) + points;
      });
  }
  return scores;
}
